import { canonicalE164, parsePhone, toE164 } from './phoneNumber';

/**
 * Estado de EDICIÓN del control de teléfono, separado del valor que se
 * persiste. Módulo puro (sin React) a propósito: acá vive toda la lógica del
 * control —`PhoneField` es un `useState` y dos handlers sobre esto— y acá se
 * prueban las interacciones (tipear de a un dígito, elegir país antes que
 * número, cambiar de país a mitad de camino) sin DOM, que es la política de
 * tests del frontend.
 *
 * Por qué hace falta: el valor del pasajero es E.164 o vacío, y mientras el
 * número está incompleto es vacío. Si el control derivara lo que muestra de
 * ese valor, cada dígito tipeado desaparecería (vacío → parse → nada) y el
 * país elegido con el número vacío no tendría dónde vivir. El estado del
 * control es lo que el asesor ve; lo emitido es lo que el booking guarda.
 */
export interface PhoneEditorState {
  /** ISO-2 elegido en el selector. Vacío = sin elegir. */
  countryCode: string;
  /** Texto del campo local, tal cual lo tipea el asesor. */
  national: string;
  /**
   * Lo último que se le entregó al padre (E.164 o vacío). Es lo que permite
   * distinguir "el padre cambió el valor" (hidratar) de "es el eco de lo que
   * emitimos" (no tocar lo que el asesor está tipeando).
   */
  emitted: string;
}

export interface PhoneEditorStep {
  state: PhoneEditorState;
  /** Valor a entregar al padre. Ausente = lo entregado no cambió. */
  emit?: string;
}

export type PhoneEditorStatus =
  | 'no_country'
  | 'empty'
  | 'incomplete'
  | 'complete';

const digitsOf = (value: string): string => value.replace(/\D/g, '');

/** El valor que corresponde a lo que se ve: E.164 canónico o vacío. */
const serialize = (countryCode: string, national: string): string =>
  toE164({ countryCode, national }) ?? '';

const step = (
  prev: PhoneEditorState,
  countryCode: string,
  national: string,
): PhoneEditorStep => {
  const value = serialize(countryCode, national);
  // Un internacional completo pegado en el campo local (`+54 11…`) manda
  // sobre el país elegido: el código pasa al selector y el campo queda con el
  // número nacional, para que lo que se ve coincida con lo que se guarda.
  const pasted =
    value && national.trim().startsWith('+')
      ? parsePhone(value, countryCode)
      : undefined;
  const state: PhoneEditorState = {
    countryCode: pasted?.countryCode || countryCode,
    national: pasted ? pasted.national : national,
    emitted: value,
  };
  return value === prev.emitted ? { state } : { state, emit: value };
};

/** El asesor tipea en el campo local. El texto se conserva tal cual. */
export function typeNational(
  prev: PhoneEditorState,
  national: string,
): PhoneEditorStep {
  return step(prev, prev.countryCode, national);
}

/**
 * Adopta el país que llega TARDE —el de la agencia, que sale de una query—
 * sobre el que se había adivinado del idioma del navegador.
 *
 * El estado no dice de DÓNDE salió el país que tiene, y esa era la pieza que
 * faltaba: con solo mirar `countryCode` no se distingue "lo eligió el asesor"
 * de "lo adivinamos del navegador mientras esperábamos". Sin esa distinción el
 * control se quedaba con la adivinanza para siempre, y un Chrome en inglés
 * seguía ofreciendo +1 en una agencia uruguaya (#2374).
 *
 * Solo pisa la adivinanza: con un país elegido a mano o un número ya tipeado
 * no toca nada. Y nunca emite — el número sigue vacío, así que el valor que ve
 * el padre no cambia.
 */
export function adoptDefaultCountry(
  prev: PhoneEditorState,
  country: string,
  chosenByUser: boolean,
): PhoneEditorStep {
  if (chosenByUser || prev.national.trim()) return { state: prev };
  return selectCountry(prev, country);
}

/** El asesor elige país. El número tipeado hasta ahí se conserva. */
export function selectCountry(
  prev: PhoneEditorState,
  countryCode: string,
): PhoneEditorStep {
  return step(prev, countryCode, prev.national);
}

/**
 * Estado inicial, o re-sincronización cuando el valor cambia DESDE AFUERA
 * (pax sugerido de la venta, pasajeros iniciales). Nunca se llama por el eco
 * de lo que el propio control emitió.
 *
 * - E.164 válido: se parte para editar. Si no era canónico (`+598096236320`),
 *   se emite el canónico para que la fila quede satisfecha en vez de "completa
 *   pero faltante".
 * - Sin `+` pero válido con `+` adelante (`59896236320`, como guarda el CRM el
 *   WhatsApp): se adopta como internacional. Mismo truco que `client-api`.
 * - Cualquier otra cosa: los dígitos se conservan y NO se adivina el país —
 *   se pide elegirlo. Un `096236320` viejo bajo el país del navegador sería
 *   un número válido inventado.
 *
 * `preferredCountry` desempata códigos compartidos (+1) y es el país que ya
 * estaba elegido, para que un valor externo no pise una elección explícita.
 */
export function hydratePhoneEditor(
  value: string | undefined,
  preferredCountry: string,
): PhoneEditorStep {
  const raw = (value ?? '').trim();
  if (!raw) {
    return {
      state: { countryCode: preferredCountry, national: '', emitted: '' },
    };
  }

  const canonical =
    canonicalE164(raw) ??
    (raw.startsWith('+') ? undefined : canonicalE164(`+${digitsOf(raw)}`));
  if (canonical) {
    const parts = parsePhone(canonical, preferredCountry);
    const state: PhoneEditorState = {
      countryCode: parts.countryCode,
      national: parts.national,
      emitted: canonical,
    };
    return canonical === (value ?? '') ? { state } : { state, emit: canonical };
  }

  if (raw.startsWith('+')) {
    const parts = parsePhone(raw, preferredCountry);
    return {
      state: {
        countryCode: parts.countryCode || preferredCountry,
        national: parts.national,
        emitted: value ?? '',
      },
    };
  }

  return {
    state: { countryCode: '', national: digitsOf(raw), emitted: value ?? '' },
  };
}

/** Qué le decimos al asesor debajo del campo. */
export function phoneEditorStatus(state: PhoneEditorState): PhoneEditorStatus {
  if (!state.countryCode) return 'no_country';
  if (!state.national.trim()) return 'empty';
  return serialize(state.countryCode, state.national) ? 'complete' : 'incomplete';
}
